import { CookieRadarResult } from '@dieterApi/scan/types';
import { ScanResultData } from '@dieterApi/scan/useScanResultQuery';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Collapse, Tooltip } from '@mui/material';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { buildScoreTooltip, ScoreMini } from './scanResultShared';
import { ScanResultDisplay } from './ScanResultDisplay';

interface ScanRowProps {
  sr: ScanResultData;
  live: ScanResultData | null;
  parsed: CookieRadarResult | null;
  isExpanded: boolean;
  noScansLeft: boolean;
  timedOut: boolean;
  runningScanId: string | null;
  abortScanLoading: boolean;
  onToggleExpanded: (propertyId: string) => void;
  onOpenReportView: (propertyId: string) => void;
  onOpenPrintView: (propertyId: string) => void;
  onRequestRemoveScan: (propertyId: string) => void;
}

function ScanRowComponent({
  sr,
  live,
  parsed,
  isExpanded,
  noScansLeft,
  timedOut,
  runningScanId,
  abortScanLoading,
  onToggleExpanded,
  onOpenReportView,
  onOpenPrintView,
  onRequestRemoveScan,
}: ScanRowProps) {
  const { t, i18n } = useTranslation();

  const isFinished = live ? live.scanFinished : sr.scanFinished;
  const score = parsed?.site?.privacy_score ?? null;
  const normalizedStatus = parsed?.status?.toLowerCase() ?? '';
  const isFailed = normalizedStatus === 'failed' || normalizedStatus === 'cancelled';
  const isQueued = normalizedStatus === 'queued';
  const isRunning = !isFinished && !isFailed && !isQueued;
  const rowData = live ?? sr;

  // Progress from live poll data - only show from 1/x onward when actively scanning
  const progressCurrent = isRunning ? (parsed?.run?.progress_current ?? null) : null;
  const progressTotal = isRunning ? (parsed?.run?.progress_total ?? null) : null;
  const showDeterminateProgress =
    isRunning &&
    progressCurrent != null &&
    progressTotal != null &&
    progressTotal > 0 &&
    progressCurrent >= 1;
  const progressPercent = showDeterminateProgress
    ? Math.min(100, Math.round((progressCurrent / progressTotal) * 100))
    : null;
  const scoreTooltip = useMemo(
    () => buildScoreTooltip(parsed, rowData.dpsUrl, t),
    [parsed, rowData.dpsUrl, t],
  );

  return (
    <div
      className={`text-left border rounded-md px-2.5 py-1.5 transition-colors ${
        isExpanded ? 'border-primary-root bg-primary-50' : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <button type="button" onClick={() => onToggleExpanded(sr.propertyId)} className="w-full text-left">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-gray-400 shrink-0">
              {isExpanded ? <ExpandLessIcon fontSize="inherit" /> : <ExpandMoreIcon fontSize="inherit" />}
            </span>
            {scoreTooltip ? (
              <Tooltip title={scoreTooltip} placement="top" arrow>
                <span><ScoreMini score={score} /></span>
              </Tooltip>
            ) : (
              <ScoreMini score={score} />
            )}
            <span className="font-medium truncate text-sm">{sr.startUrl}</span>
          </div>
          <span
            className={`text-xs shrink-0 ${
              isFailed
                ? 'text-red-600'
                : isFinished
                  ? 'text-green-600'
                  : isQueued
                    ? 'text-slate-600'
                    : 'text-amber-600'
            }`}
          >
            {isFailed
              ? t('route.scanresult.status.failed')
              : isFinished
                ? t('route.scanresult.status.completed')
                : isQueued
                  ? t('route.scanresult.status.queued')
                  : t('route.scanresult.status.running')}
          </span>
        </div>

        {/* Progress indicator for running scans - only show from 1/x onward */}
        {showDeterminateProgress && (
          <div className="mt-1">
            <div className="text-xs text-gray-600 flex items-center gap-2 mb-1">
              <span>
                {progressCurrent} / {progressTotal}
                {progressPercent != null && ` (${progressPercent}%)`}
              </span>
            </div>
            <div className="bg-gray-200 rounded-full h-1">
              <div
                className="bg-blue-500 h-1 rounded-full transition-all duration-300"
                style={{ width: `${progressPercent ?? 0}%` }}
              />
            </div>
          </div>
        )}

        {/* Timestamp for finished scans */}
        {isFinished && (
          <div className="text-xs text-gray-400 mt-0.5">
            {new Date(sr.createdAt).toLocaleString(i18n.resolvedLanguage || i18n.language || undefined)}
          </div>
        )}
      </button>

      <div className="mt-1 flex justify-end gap-2">
        {isFinished && (
          <>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onOpenReportView(sr.propertyId);
              }}
              className="text-xs text-primary-root hover:text-primary-700 inline-flex items-center gap-1"
            >
              <OpenInNewIcon fontSize="inherit" />
              {t('route.scanresult.open_report_view')}
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onOpenPrintView(sr.propertyId);
              }}
              className="text-xs text-primary-root hover:text-primary-700 inline-flex items-center gap-1"
            >
              <OpenInNewIcon fontSize="inherit" />
              {t('route.scanresult.open_print_view')}
            </button>
          </>
        )}
        {(isRunning || isQueued) && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRequestRemoveScan(sr.propertyId);
            }}
            disabled={abortScanLoading}
            className="text-xs text-amber-500 hover:text-amber-700 inline-flex items-center gap-1"
          >
            <CancelIcon fontSize="inherit" />
            {t('route.scanresult.cancel_scan')}
          </button>
        )}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRequestRemoveScan(sr.propertyId);
          }}
          disabled={abortScanLoading}
          className="text-xs text-gray-400 hover:text-red-600 inline-flex items-center gap-1"
        >
          <DeleteOutlineIcon fontSize="inherit" />
          {t('route.scanresult.remove_from_list')}
        </button>
      </div>

      <Collapse in={isExpanded} unmountOnExit>
        <div className="mt-3 border-t border-gray-200 pt-3">
          {isExpanded && (
            <ScanResultDisplay
              data={rowData}
              startUrl={rowData.startUrl || sr.startUrl}
              loading={!isFinished}
              noScansLeft={noScansLeft}
              timedOut={timedOut && !isFinished && runningScanId === sr.propertyId}
            />
          )}
        </div>
      </Collapse>
    </div>
  );
}

export const ScanRow = memo(ScanRowComponent);
